/**
 * Address entry guards for the Contacts forms (2026-10-27, Matt).
 *
 * People paste a whole address ("100 Marina Dr, Needles, CA 92363") into the Street or City box,
 * and the Organizations table ended up with nine records whose City held a street address or a
 * state — the address then printed twice. splitAddress() turns a pasted full address into the four
 * boxes; cityProblem() refuses a City that is not just a city name. Pure, no data access.
 */

export const STATE_ABBR = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas',
  KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts',
  MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
  NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
}

const STATE_NAMES = Object.values(STATE_ABBR)

/* The full state name for "CA", "Ca.", "california", or null. */
function stateName(word) {
  const w = String(word || '').replace(/\./g, '').trim()
  if (!w) return null
  const ab = STATE_ABBR[w.toUpperCase()]
  if (ab) return ab
  return STATE_NAMES.find(n => n.toLowerCase() === w.toLowerCase()) || null
}

/* Pull a trailing "<state> <zip>" off text. Returns { rest, state, zip } or null when it doesn't end
   in a state. The state may be one or more words ("New York"); the zip is optional. */
function takeStateZip(text) {
  let t = text.trim().replace(/[,\s]+$/, '')
  let zip = ''
  const zm = t.match(/[\s,]+(\d{5}(?:-\d{4})?)$/)
  if (zm) { zip = zm[1]; t = t.slice(0, zm.index) }
  t = t.replace(/[,\s]+$/, '')
  // Try the last three, two, then one word as the state.
  const words = t.split(/\s+/)
  for (let n = Math.min(3, words.length - 1); n >= 1; n--) {
    const st = stateName(words.slice(-n).join(' '))
    if (st) return { rest: words.slice(0, -n).join(' ').replace(/[,\s]+$/, ''), state: st, zip }
  }
  return null
}

/**
 * Split a pasted full address into { street, city, state, zip }. Returns null when the text isn't a
 * full address (no state, or no comma between the street and the city) so the paste goes in as typed.
 */
export function splitAddress(text) {
  const oneLine = String(text || '').replace(/\s*\n\s*/g, ', ').trim()
  if (!oneLine.includes(',')) return null
  const tail = takeStateZip(oneLine)
  if (!tail || !tail.rest) return null
  const cut = tail.rest.lastIndexOf(',')
  if (cut < 0) return null
  const street = tail.rest.slice(0, cut).trim()
  const city = tail.rest.slice(cut + 1).trim()
  if (!street || !city || /\d/.test(city)) return null
  return { street, city, state: tail.state, zip: tail.zip }
}

/** Why this City can't be saved, or null when it's fine. */
export function cityProblem(city) {
  const c = String(city || '').trim()
  if (!c) return null
  if (/\d/.test(c)) return 'City has a number in it. Put the street address in Street and only the city name in City.'
  if (c.includes(',')) return 'City should be the city name only, without the state, ZIP or a comma.'
  const words = c.split(/\s+/)
  if (words.length > 1 && STATE_ABBR[words[words.length - 1]]) {
    return 'City should be the city name only. The state goes in the State box.'
  }
  return null
}
